import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, Sort } from '@angular/material/sort';
import { Subject, takeUntil } from 'rxjs';
import { ConfirmationModalComponent } from 'src/app/shared/confirmation-modal/confirmation-modal.component';
import { CustomerTypeTranslations } from './customer-type-translations';
import { Router } from '@angular/router';
import { Platform } from '@angular/cdk/platform';
import { MatPaginator } from '@angular/material/paginator';
import { PaginationResult, PaginatorParams } from '@api/models/paginator';
import { Customer, CustomerApi } from '@api/index';
import { GenericDialogComponent } from '@app/shared/generic-dialog/generic-dialog.component';
import { CustomerParams } from './model/customer.model';
import { TranslateService } from '@ngx-translate/core';
import { MatTableDataSource } from '@angular/material/table';
import { buildCountryMap } from '@shared/utils/utils';

@Component({
  selector: 'flow-customer',
  templateUrl: './customer.component.html',
  styleUrls: ['./customer.component.scss'],
})
export class CustomerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  protected destroy$ = new Subject<void>();

  displayedColumns: string[] = [
    'id',
    'name',
    'organizationId',
    'type',
    'country',
    'person',
    'presentationTime',
    'numbersOfWeekForFiltering',
    'storageLimitSize',
    'poiLimit',
    'contactPersonLimit',
    'actions',
  ];
  dataSource = new MatTableDataSource<Customer>();
  CustomerTypeTranslations = CustomerTypeTranslations;
  hideBox = false;
  params: PaginatorParams = {
    pageNumber: 0,
    pageSize: 10,
    sortBy: 'name',
    sortDirection: 'asc',
  };
  totalCount: number = 0;
  pageSizeOptions = [5, 10, 25, 100];
  countryMap: Record<string, string> = {};

  constructor(
    private customerApi: CustomerApi,
    public dialog: MatDialog,
    private router: Router,
    public platform: Platform,
    private translateService: TranslateService
  ) {}
  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  ngOnInit(): void {
    const currentLang = this.translateService.currentLang;
    this.countryMap = buildCountryMap(currentLang);
    this.getCustomers(this.params);
  }

  getCustomers(params?: CustomerParams): void {
    this.customerApi
      .getCustomersList(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: PaginationResult<Customer>) => {
        const customers = response?.items.map(c => ({
          ...c,
          country: this.countryMap[c.country] || c.country,
        }));
        this.dataSource = new MatTableDataSource<Customer>(customers);
        this.params = { ...this.params, pageNumber: response?.currentPage };
        this.totalCount = response?.totalCount;
      });
  }

  onDeleteClick(event: Event, customer: Customer): void {
    event.preventDefault();
    event.stopPropagation();
    this.dialog.open(ConfirmationModalComponent, {
      width: '500px',
      data: {
        remove: () => this.onRemoveCustomer(customer.id),
        logic: true,
      },
    });
  }

  private onRemoveCustomer(customerId: number) {
    this.customerApi
      .removeCustomer(customerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.dialog.closeAll();
        this.getCustomers(this.params);
      });
  }

  redirectToEditPage(customerId: number): void {
    this.router.navigate(['/customers', customerId]);
  }

  contactPersonLabel(customer: Customer): string {
    return customer.contactPersons?.map(c => c.name)?.join(', ');
  }

  handleSortEvent(sort: Sort) {
    this.params = {
      ...this.params,
      pageNumber: 0,
      sortBy: sort?.active,
      sortDirection: sort.direction || (this.params.sortDirection == 'asc' ? 'desc' : 'asc') || 'asc',
    };
    this.getCustomers(this.params);
  }

  handlePageEvent(e) {
    this.params = { ...this.params, pageNumber: e.pageIndex, pageSize: e.pageSize };
    this.getCustomers(this.params);
  }

  openCustomerDialog(users: any[]): void {
    const dialogData = {
      title: 'contactPerson.title',
      type: 'list',
      items: users.map(user => user.name),
    };

    const dialogRef = this.dialog.open(GenericDialogComponent, {
      width: '400px',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
    });
  }

  searchCustomers(params: CustomerParams): void {
    this.params = {
      ...this.params,
      ...params,
      pageNumber: 0,
    };

    this.getCustomers(this.params);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
